import { listenerMiddleware } from './listener.middleware';
import {
  consultationStatsApi,
  intubationApi,
  labTestApi,
  medicalDocumentApi,
  medicalHoApi,
  medsApi,
  physicalExaminationApi,
  surgeryInfoApi,
  WithPatientId
} from '@/modules/surgicalEvent';
import { AnyAction, isAnyOf, ListenerEffectAPI } from '@reduxjs/toolkit';
import { AutomationActionType, AutomationService } from '@/modules/automation';
import {
  PersistSuggestionPayload,
  scratchSuggestionApi
} from '@/modules/scratchConsultation';
import { constructProtocolCacheKey, ProtocolName } from '@/modules/protocol';
import { ID } from '@ianesthesie-kit/services';
import { captureThrowOnDev } from '@/util';
import { SentryService } from '@ianesthesie-kit/sentry';
import { doctorBffApi, InvalidateArr } from '@/modules';
// For some reason build breaks if medicalScoresApi is imported from @/modules/surgicalEvent
import { medicalScoresApi } from '@/modules/surgicalEvent/scores';

// PHYSICAL_EXAMINATION(_CARDIO_VASCULAR)
listenerMiddleware.startListening({
  matcher: isAnyOf(
    physicalExaminationApi.endpoints.patchPhysicalExamination.matchFulfilled
  ),
  effect: async (action, api) => {
    const { patientId } = (action.meta as any).arg.originalArgs;
    await invalidateIfNeeded(patientId, api, action);
  }
});

// SURGERY_INFO_SURGERIES
// ASA_CLASS
listenerMiddleware.startListening({
  matcher: isAnyOf(surgeryInfoApi.endpoints.patchSurgeryInfo.matchFulfilled),
  effect: async (action, api) => {
    const { patientId, surgeries, asaScore } = (action.meta as any).arg
      .originalArgs;
    if (surgeries === undefined && asaScore === undefined) {
      return;
    }

    await invalidateIfNeeded(patientId, api, action);
  }
});

listenerMiddleware.startListening({
  matcher: isAnyOf(
    // CONSULTATION_STATS_WEIGHT_KG & CONSULTATION_STATS_HEIGHT_CM
    consultationStatsApi.endpoints.upsertConsultationStat.matchFulfilled,
    // MEDICAL_SCORES
    medicalScoresApi.endpoints.updateScore.matchFulfilled,
    medicalScoresApi.endpoints.deleteScore.matchFulfilled,
    // MEDICAL_DOCUMENTS
    medicalDocumentApi.endpoints.upsertMedicalDocument.matchFulfilled,
    // MHO_ITEMS
    medicalHoApi.endpoints.addHoItem.matchFulfilled,
    medicalHoApi.endpoints.deleteHoItem.matchFulfilled,
    scratchSuggestionApi.endpoints.persistSuggestion.matchFulfilled,
    // LAB_RESULT_ITEMS
    labTestApi.endpoints.patchLabTest.matchFulfilled,
    labTestApi.endpoints.deleteLabTest.matchFulfilled,
    // MEDICATIONS
    medsApi.endpoints.upsertMed.matchFulfilled,
    medsApi.endpoints.deleteMed.matchFulfilled,
    // INTUBATION_INFO
    intubationApi.endpoints.patchIntubationInfo.matchFulfilled
  ),
  effect: async (action, api) => {
    const payload = (action.meta as any).arg.originalArgs as
      | PersistSuggestionPayload
      | WithPatientId<unknown>;
    const patientId =
      'patientId' in payload ? payload.patientId : payload.activeSe.patient.id;

    await invalidateIfNeeded(patientId, api, action);
  }
});

async function invalidateIfNeeded(
  patientId: ID,
  api: ListenerEffectAPI<unknown, any>,
  action: AnyAction
) {
  if (!patientIdValidOrReport(patientId, action)) {
    return;
  }
  const latestExecutions = await AutomationService.getInstance(
    patientId
  ).fetchLatestAutomationExecutions();

  const actionTypes = new Set(latestExecutions.data.map((ex) => ex.actionType));

  if (actionTypes.size) {
    api.dispatch(
      doctorBffApi.util.invalidateTags(['AutomationActionExecutionLogs'])
    );
    actionTypes.forEach((t) => {
      api.dispatch(
        doctorBffApi.util.invalidateTags(getTagsToInvalidate(t, patientId))
      );
    });
  }
}

function patientIdValidOrReport(patientId: ID, action: AnyAction) {
  if (!patientId) {
    SentryService.getInstance().addBreadcrumb({
      type: 'warning',
      message: 'Could not extract patientId from action',
      data: action
    });
    captureThrowOnDev(new Error('Could not extract patientId from action'));
    return false;
  }

  return true;
}

function getTagsToInvalidate(
  actionType: AutomationActionType,
  patientId: ID
): InvalidateArr {
  switch (actionType) {
    case 'UPDATE_NUTRITION_SUPPORT_PROTOCOL_STATUS':
      return [
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            protocol: ProtocolName.NUTRITION,
            patientId
          })
        },
        { type: 'ProtocolStatuses', id: patientId },
        'Dashboard'
      ];
    case 'UPDATE_CARDIAC_STRESS_TEST_PROTOCOL_STATUS':
      return [
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            protocol: ProtocolName.CARDIAC_STRESS_TEST,
            patientId
          })
        },
        { type: 'ProtocolStatuses', id: patientId },
        'Dashboard'
      ];
    case 'ADD_MHO':
    case 'REMOVE_MHO':
      return [
        {
          type: 'MedicalHo',
          id: patientId
        },
        {
          type: 'PhysicalExamination',
          id: patientId
        }
      ];
    case 'SET_AMBULATORY':
      return [{ type: 'SurgeryInfo', id: patientId }];

    case 'UPDATE_SURGERY_PLANNING_PROTOCOL_STATUS':
      return [
        'Dashboard',
        { type: 'ProtocolStatuses', id: patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            protocol: ProtocolName.SURGERY_PLANNING,
            patientId
          })
        }
      ];
    case 'UPDATE_ANESTHESIA_INFO':
      return [
        'Dashboard',
        { type: 'AnesthesiaInfo', id: patientId },
        { type: 'AnesthesiaRisk', id: patientId }
      ];
    case 'SET_CARE_TYPE':
      return ['Dashboard', { type: 'SurgeryInfo', id: patientId }];
    case 'ADD_LAB_ORDER_ITEM_CODE':
      return [
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            protocol: ProtocolName.PREOP_LAB_TEST,
            patientId
          })
        },
        { type: 'ProtocolStatuses', id: patientId }
      ];
    case 'SET_PRIORITY':
      return ['Dashboard', { type: 'ActiveSurgicalEvent', id: patientId }];

    case 'ADD_CARDIO':
    case 'REMOVE_CARDIO':
      return [
        {
          type: 'PhysicalExamination',
          id: patientId
        },
        {
          type: 'MedicalHo',
          id: patientId
        }
      ];

    default:
      captureThrowOnDev(
        new Error(`Unexpected automation action type: ${actionType}`)
      );
      return [];
  }
}
